import React, { useEffect, useState } from "react";
import { LoaderCircle, MousePointerClick, Trophy, Package } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

export const ClicksTab = () => {
    const [products, setProducts] = useState(null);

    useEffect(() => {
        api.get("/products").then(({ data }) => setProducts(data)).catch((e) => toast.error(formatApiError(e)));
    }, []);

    if (!products) return <div className="flex justify-center py-16"><LoaderCircle className="animate-spin text-[#718370]" /></div>;

    const data = products.map((p) => ({
        name: p.name.length > 24 ? `${p.name.slice(0, 24)}…` : p.name,
        full: p.name,
        clicks: p.clicks ?? 0,
        pick: !!p.recommended || !!p.highlighted,
    }));
    const total = data.reduce((a, d) => a + d.clicks, 0);
    const best = data.reduce((m, d) => (d.clicks > (m?.clicks ?? -1) ? d : m), null);

    return (
        <div className="space-y-6" data-testid="clicks-tab">
            <div className="grid gap-4 sm:grid-cols-3">
                <div className="rounded-2xl border border-[#E4DFD3] bg-white p-5">
                    <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#6D736D]">
                        <MousePointerClick size={14} /> Total clicks
                    </div>
                    <p className="mt-2 font-serif text-3xl font-semibold text-[#1A1D1A]" data-testid="clicks-total">{total}</p>
                </div>
                <div className="rounded-2xl border border-[#E4DFD3] bg-white p-5">
                    <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#6D736D]">
                        <Trophy size={14} /> Best performer
                    </div>
                    <p className="mt-2 truncate font-serif text-lg font-semibold text-[#1A1D1A]" data-testid="clicks-best">
                        {best && best.clicks > 0 ? best.full : "—"}
                    </p>
                </div>
                <div className="rounded-2xl border border-[#E4DFD3] bg-white p-5">
                    <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#6D736D]">
                        <Package size={14} /> Products tracked
                    </div>
                    <p className="mt-2 font-serif text-3xl font-semibold text-[#1A1D1A]">{data.length}</p>
                </div>
            </div>

            <div className="rounded-2xl border border-[#E4DFD3] bg-white p-5">
                <h3 className="font-serif text-lg font-semibold text-[#1A1D1A]">Clicks per product</h3>
                <p className="mt-1 text-xs text-[#6D736D]">Counted each time a visitor taps a “View on Amazon” button. Darker bars are your highlighted picks.</p>
                <div className="mt-4 h-72" data-testid="clicks-chart">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={data} layout="vertical" margin={{ left: 8, right: 24, top: 4, bottom: 4 }}>
                            <XAxis type="number" allowDecimals={false} stroke="#6D736D" fontSize={12} tickLine={false} axisLine={{ stroke: "#E4DFD3" }} />
                            <YAxis type="category" dataKey="name" width={180} stroke="#6D736D" fontSize={12} tickLine={false} axisLine={false} />
                            <Tooltip
                                cursor={{ fill: "#F5F2EA" }}
                                contentStyle={{ background: "#222522", border: "none", borderRadius: 12, color: "#FDFCF8", fontSize: 13 }}
                                labelStyle={{ color: "#9AB098", fontWeight: 700 }}
                                formatter={(value, _n, item) => [`${value} clicks`, item?.payload?.full || "Product"]}
                                labelFormatter={() => ""}
                            />
                            <Bar dataKey="clicks" radius={[0, 6, 6, 0]} maxBarSize={26}>
                                {data.map((d, i) => (
                                    <Cell key={i} fill={d.pick ? "#576856" : "#9AB098"} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </div>
    );
};
